import DOMPurify from "isomorphic-dompurify";

/**
 * Sanitize tenant-authored HTML before it goes into `dangerouslySetInnerHTML`.
 *
 * This function returned its input unchanged for a long time (the sanitizer was
 * commented out), so every one of its ~13 call sites — CMS pages, blog posts,
 * product specifications, checkout method hints, terms modals — was a stored-XSS
 * sink: anything a tenant could type into their dashboard executed in every
 * shopper's session on that store.
 *
 * `isomorphic-dompurify` is used rather than plain `dompurify` because several
 * call sites are SERVER components (`app/[lang]/inner-pages/[slug]/page.tsx`,
 * `HtmlBlock`), where there is no `window` for DOMPurify to bind to.
 *
 * WHAT IS DELIBERATELY STILL ALLOWED, and why:
 *  - `<iframe>` from the embed hosts below. DOMPurify drops iframes by default,
 *    which would silently delete every tenant's embedded video or map. The `src`
 *    is checked against an allowlist AFTER attribute sanitization, so a
 *    `javascript:` or data-URI iframe is still removed.
 *  - inline `style` attributes. Tenants lay out their CMS blocks with them;
 *    DOMPurify already strips `expression()` and url(javascript:) from CSS.
 *
 * Scripts are NOT allowed here and that costs us nothing: `HtmlBlock` carries
 * tenant scripts in a separate `data.scripts` field rendered through
 * `ScriptInject`, so legitimate tenant scripting keeps working untouched.
 */
const EMBED_HOSTS =
  /^https:\/\/(?:[\w-]+\.)*(?:youtube\.com|youtube-nocookie\.com|youtu\.be|vimeo\.com|dailymotion\.com|google\.com|gstatic\.com|soundcloud\.com|spotify\.com|facebook\.com|instagram\.com|tiktok\.com|streamable\.com|joinin2\.com)(?:[/?#]|$)/i;

// Mozilla's hosted pdf.js viewer, which tenants use to embed PDFs in blog posts
// (`viewer.html?file=<pdf url>`). Pinned to the exact viewer path rather than
// the host: anyone can publish a page on `*.github.io`.
const PDF_VIEWER =
  /^https:\/\/mozilla\.github\.io\/pdf\.js\/web\/viewer\.html(?:[?#]|$)/i;

const isAllowedEmbed = (src: string) =>
  EMBED_HOSTS.test(src) || PDF_VIEWER.test(src);

// Registered once at module scope, not per call: `addHook` appends, so hooking
// inside `safeHTML` would stack a new listener on every render.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeName === "IFRAME") {
    const src = node.getAttribute("src") ?? "";
    // Anything not on the embed allowlist is removed entirely rather than left
    // as an empty frame.
    if (!isAllowedEmbed(src)) node.remove();
    return;
  }
  // A tenant link opening in a new tab must not hand the opener window over.
  if (node.nodeName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export const safeHTML = (html: string) =>
  DOMPurify.sanitize(html ?? "", {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "loading",
      "referrerpolicy",
      "target",
    ],
    // Keep `<html>`/`<body>` wrappers out of the output — these fragments are
    // injected into an existing element.
    WHOLE_DOCUMENT: false,
  });

export const safeCSS = (css?: string) =>
  (css ?? "").replace(/<\/?style\b[^>]*>/gi, "").trim();

/**
 * Mark every `<img>` in a block of tenant-authored HTML as lazy + async-decoded,
 * unless the author already set `loading` themselves.
 *
 * Tenant HTML is injected verbatim, so these images get none of the treatment
 * our own components apply: they are raw, full-size, and eagerly fetched no
 * matter how far below the fold they sit. On this storefront the footer's
 * background alone is a 331KB PNG that competes with the hero for bandwidth on
 * a slow connection.
 *
 * Only call this for blocks that are NOT the first section — lazy-loading an
 * above-the-fold image would delay LCP instead of helping it.
 */
// Attribute run of an `<img>` tag: a sequence of quoted strings or plain
// characters. Consuming quoted strings as a unit is what makes a raw `>` inside
// an attribute value safe — `alt="Save 50% -> today"` previously ended the match
// early, so an author's `loading="eager"` went unseen and we emitted a SECOND
// `loading` attribute. Duplicate attributes are invalid and the first one wins,
// so `lazy` silently overrode `eager` — an LCP regression from the LCP fix.
const IMG_TAG = /<img\b((?:"[^"]*"|'[^']*'|[^>"'])*)>/gi;
const HAS_LOADING = /(^|\s)loading\s*=/i;
const HAS_DECODING = /(^|\s)decoding\s*=/i;

export const lazyLoadImages = (html: string) =>
  html.replace(IMG_TAG, (match, attrs: string) => {
    if (HAS_LOADING.test(attrs)) return match;
    const decoding = HAS_DECODING.test(attrs) ? "" : ' decoding="async"';
    return `<img loading="lazy"${decoding}${attrs}>`;
  });
